import { useForm } from 'react-hook-form'
import { ReactElement, useCallback } from 'react'

import { z } from 'zod';
import { format, isValid, parse } from 'date-fns'

import { Botao, Cores, Corpo, InputData, Textfield } from '@componentes-mu/componentes-mu'
import { zodResolver } from '@hookform/resolvers/zod';

import styles from './styles.module.scss'
import { Contact } from '../../types/cliente/contatos/types'
import { ColumnSort, ReleasedVersion, ReleasedVersionsFilters, ReleasedVersionsResponse } from '../../types/cliente/versoes/types'
import Table from '../../components/Table'
import { Column, SortDirection } from '../../components/Table/types'
import { FlexColumn, FlexRow } from '../../components/Flex'
import { Select, SelectOption } from '../../components/Select'
import { Actions } from './Actions'
import { ModalType } from './VersoesLiberadasContainer'

const schema = z.object({
  dataInicial: z.string().optional(),
  dataFinal: z.string().optional(),
  dataInicialLiberacao: z.string().optional(),
  dataFinalLiberacao: z.string().optional(),
  descricaoTopico: z.string().optional(),
  numFicha: z.number().optional(),
  oidFicha: z.number().optional(),
  oidContato: z.number().optional(),
  pagina: z.number().optional(),
  tamanhoPagina: z.number().optional(),
}).refine((data) => {
  if (data.dataInicial && data.dataFinal) {
    const parsedDataInicial = parse(data.dataInicial, 'dd/MM/yyyy', new Date());
    const parsedDataFinal = parse(data.dataFinal, 'dd/MM/yyyy', new Date());
    return isValid(parsedDataInicial) && isValid(parsedDataFinal) && parsedDataFinal >= parsedDataInicial;
  }
  return true;
}, {
  message: "Data final (Ficha) deve ser maior que a data inicial (Ficha)",
  path: ["dataFinal"],
}).refine((data) => {
  if (data.dataInicialLiberacao && data.dataFinalLiberacao) {
    const parsedDataInicialLiberacao = parse(data.dataInicialLiberacao, 'dd/MM/yyyy', new Date());
    const parsedDataFinalLiberacao = parse(data.dataFinalLiberacao, 'dd/MM/yyyy', new Date());
    return isValid(parsedDataInicialLiberacao) && isValid(parsedDataFinalLiberacao) && parsedDataFinalLiberacao >= parsedDataInicialLiberacao;
  }
  return true;
}, {
  message: "Data final (Liberação) deve ser maior que a data inicial (Liberação)",
  path: ["dataFinalLiberacao"],
});

type ReleasedVersionsProps = {
  filters: ReleasedVersionsFilters
  contacts: Contact[]
  data: ReleasedVersionsResponse
  onPageChange: (page: number) => void,
  onItemsPerPageChange: (itemsPerPage: number) => void,
  onFilter: (data: ReleasedVersionsFilters) => void
  handleOption: (modalType: ModalType, releasedVersion: ReleasedVersion) => void
  onSort: (conatiner: string, direction: SortDirection) => void
}


const VersoesLiberadas = ({ data, contacts, filters, onPageChange, onItemsPerPageChange, onFilter, handleOption, onSort }: ReleasedVersionsProps) => {

  const { setValue, watch, handleSubmit, formState: { errors } } = useForm<ReleasedVersionsFilters>({
    defaultValues: {
      ...filters
    },
    resolver: zodResolver(schema),
  });

  const getContactsOptions = (contacts: Contact[]): SelectOption[] => [
    { value: '', label: 'Selecione...' },
    ...contacts.map((contact) => ({
      value: contact.oid,
      label: contact.nome,
    })),
  ];

  const contactsOptions = getContactsOptions(contacts)

  const renderContainer = useCallback((conatiner: string, original: ReleasedVersion) => {
    const conatinerOnj: Record<string, ReactElement> = {
      data: (
        <>{format(new Date(original.data), 'dd/MM/yyyy')}</>
      ),
      dataLiberacao: (
        <>{format(new Date(original.dataLiberacao), 'dd/MM/yyyy')}</>
      ),
      actions: (
        <Actions handleOption={(modalType) => handleOption(modalType, original)} />
      ),
    }

    return conatinerOnj[conatiner]
  }, [handleOption]);

  const columns: Column<ReleasedVersion>[] = [
    {
      key: "numFicha",
      header: "N° TICKET",
      width: '10%',
      align: "left",
      sortable: true,
       onSort:  (_, sort) => onSort('RECORD_NUMBER', sort),
      sortDirection: filters.campoOrdenado == ColumnSort.RECORD_NUMBER ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "descricaoTopico",
      header: "TÍTULO",
      width: '35%',
      align: "left",
    },
    {
      key: "data",
      header: "INCLUSÃO",
      Cell: (row) => renderContainer('data', row),
      width: '10%',
      sortable: true,
      onSort:  (_, sort) => onSort('created_at', sort),
      sortDirection: filters.campoOrdenado == ColumnSort.CREATED_AT ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "dataLiberacao",
      header: "LIBERAÇÃO",
      Cell: (row) => renderContainer('dataLiberacao', row),
      width: '10%',
      sortable: true,
      onSort:  (_, sort) => onSort('released_at', sort),
      sortDirection: filters.campoOrdenado == ColumnSort.RELEASED_AT ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "nome",
      header: "SOLICITANTE",
      width: '10%',
      sortable: true,
      onSort:  (_, sort) => onSort('requester', sort),
      sortDirection: filters.campoOrdenado == ColumnSort.REQUESTER ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "situacao",
      header: "SITUAÇÃO",
      width: '10%',
      sortable: true,
       onSort:  (_, sort) => onSort('status', sort),
      sortDirection: filters.campoOrdenado == ColumnSort.STATUS ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "versao",
      header: "VERSÃO",
      width: '10%'
    },
    {
      key: "actions",
      header: "AÇÕES",
      Cell: (row) => renderContainer('actions', row),
      width: '10%'
    },
  ];

  return (
    <>
      <form className={styles.formContainer} onSubmit={handleSubmit(onFilter)}>
        <FlexRow gap="5rem" alignItems='center'>

          <FlexColumn gap='1rem'>

            <FlexRow alignItems='center' gap='1rem'>
              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
                style={{ width: 100, textAlign: 'right' }} negrito={500}>
                Titulo
              </Corpo>
              <Textfield
                id='nomeTreinamento'
                placeholder={"Descrição"}
                valor={watch('descricaoTopico')}
                maxLength={100}
                onChange={(e) => setValue("descricaoTopico", e || '')}
                style={{ flex: 1 }}
              />
            </FlexRow>

            <FlexRow alignItems='center' gap='1rem' justifyContent='center'>
              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
                style={{ width: 100, textAlign: 'right' }} negrito={500}>
                N° do Ticket
              </Corpo>
              <Textfield
                id='numFicha'
                placeholder={"XXXXXX"}
                valor={watch('numFicha') !== undefined ? String(watch('numFicha')) : ''}
                maxLength={100}
                onChange={e => {
                  const value = e ? Number(e) : undefined;
                  setValue('numFicha', value);
                }}
                style={{ flex: 1 }}
              />

              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
                style={{ width: 100, textAlign: 'right' }} negrito={500}>
                Inclusâo
              </Corpo>

              <div style={{ maxWidth: "200px" }}>
                <InputData
                  id='dataInicial'
                  onChange={(e) => setValue("dataInicial", e || undefined)}
                  label={false}
                  placeholder={true}
                  formatoData="dd/mm/aaaa"
                  value={watch('dataInicial')}
                />
              </div>
              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>
                até
              </Corpo>
              <div style={{ maxWidth: "200px" }}>
                <InputData
                  id='dataFinal'
                  onChange={(e) => setValue("dataFinal", e || undefined)}
                  label={false}
                  placeholder={true}
                  formatoData="dd/mm/aaaa"
                  value={watch('dataFinal')}
                  variante={errors.dataFinal ? 'error' : 'regular'}
                />
              </div>

            </FlexRow>

            <FlexRow alignItems='center' gap='1rem' justifyContent='center'>

              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
                style={{ width: 100, textAlign: 'right' }} negrito={500}>
                Solicitante
              </Corpo>

              <div style={{ flex: 1 }}>
                <Select
                  value={watch('oidContato')}
                  onChange={oid => setValue("oidContato", oid ? Number(oid) : undefined)}
                  options={contactsOptions}
                />
              </div>

              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
                style={{ width: 100, textAlign: 'right' }} negrito={500}>
                Liberação
              </Corpo>

              <div style={{ maxWidth: "200px" }}>
                <InputData
                  id='dataInicialLiberacao'
                  onChange={(e) => setValue("dataInicialLiberacao", e || undefined)}
                  label={false}
                  placeholder={true}
                  formatoData="dd/mm/aaaa"
                  value={watch('dataInicialLiberacao')}
                />
              </div>

              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>até</Corpo>

              <div style={{ maxWidth: "200px" }}>
                <InputData
                  id='dataFinalLiberacao'
                  onChange={(e) => setValue("dataFinalLiberacao", e || undefined)}
                  label={false}
                  placeholder={true}
                  formatoData="dd/mm/aaaa"
                  value={watch('dataFinalLiberacao')}
                  variante={errors.dataFinalLiberacao ? 'error' : 'regular'}
                />
              </div>
            </FlexRow>
          </FlexColumn>

          <Botao tipoComportamento="submit" cor={Cores.GradacaoNeutros.CinzaFillMU100}
            tipo='tipoDefault'
            style={{ minWidth: '100px' }}
          >
            <Corpo tipo="button1" cor={Cores.CoresIdentidadeMU.azulMu} negrito={700}>
              Buscar
            </Corpo>
          </Botao>
        </FlexRow>
      </form>

      <Table
        columns={columns}
        data={data.versoesLiberadas}
        currentPage={data.paginaAtual}
        itemsPerPage={data.tamanhoPagina}
        totalItems={data.totalItens}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
      />
    </>
  )
}

export { VersoesLiberadas }
